import { UserRole } from '@/types/shared/auth';

export interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  category: ServiceRequestCategory;
  priority: ServiceRequestPriority;
  status: ServiceRequestStatus;
  requestedBy: {
    id: string;
    name: string;
    role: UserRole;
    unitNumber?: string;
    building?: string;
  };
  location: {
    building?: string;
    unit?: string;
    commonArea?: string;
  };
  budget?: {
    min?: number;
    max?: number;
    currency: string;
  };
  timeline?: {
    preferredStartDate?: string;
    deadline?: string;
    estimatedDuration?: string;
  };
  attachments: ServiceRequestAttachment[];
  responses?: ServiceRequestResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestResponse {
  id: string;
  serviceRequestId: string;
  vendorId: string;
  vendorName: string;
  vendorRating?: number;
  message: string;
  estimatedCost?: number;
  estimatedDuration?: string;
  availability?: string;
  certifications?: string[];
  portfolio?: ServiceRequestPortfolioItem[];
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestPortfolioItem {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  completedAt: string;
}

export interface ServiceRequestAttachment {
  id: string;
  name: string;
  url: string;
  type: 'photo' | 'document' | 'video';
  size: number;
  uploadedAt: string;
}

export type ServiceRequestCategory =
  | 'hvac'
  | 'plumbing'
  | 'electrical'
  | 'landscaping'
  | 'cleaning'
  | 'security'
  | 'painting'
  | 'carpentry'
  | 'general_maintenance'
  | 'pest_control'
  | 'appliance_repair'
  | 'flooring'
  | 'roofing'
  | 'emergency'
  | 'other';

export type ServiceRequestPriority = 'low' | 'medium' | 'high' | 'urgent';

export type ServiceRequestStatus =
  | 'draft' // Being created by resident
  | 'submitted' // Submitted and open for bids
  | 'open' // Alternative name for submitted (vendor perspective)
  | 'in_review' // Being reviewed by management
  | 'approved' // Approved and ready for assignment
  | 'assigned' // Vendor assigned
  | 'in_progress' // Work in progress
  | 'completed' // Work completed
  | 'cancelled' // Cancelled by resident
  | 'rejected'; // Rejected by management

export interface ServiceRequestFilters {
  category?: ServiceRequestCategory;
  priority?: ServiceRequestPriority;
  status?: ServiceRequestStatus;
  dateRange?: {
    start: string;
    end: string;
  };
  budgetRange?: {
    min: number;
    max: number;
  };
}

// Entity-based access control for service requests
export interface ServiceRequestEntityAccess {
  canView: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canRespond: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canManage: (userRole: UserRole, serviceRequest: ServiceRequest, userId?: string) => boolean;
  canCreate: (userRole: UserRole) => boolean;
}

export const serviceRequestEntityAccess: ServiceRequestEntityAccess = {
  canView: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return true;
      case 'moderator':
        return true;
      case 'vendor':
        return (
          serviceRequest.status === 'open' ||
          serviceRequest.responses?.some((r) => r.vendorId === userId)
        );
      case 'resident':
        return serviceRequest.requestedBy.id === userId;
      default:
        return false;
    }
  },

  canRespond: (userRole, serviceRequest, userId) => {
    return (
      userRole === 'vendor' &&
      serviceRequest.status === 'open' &&
      !serviceRequest.responses?.some((r) => r.vendorId === userId)
    );
  },

  canManage: (userRole, serviceRequest, userId) => {
    switch (userRole) {
      case 'super_admin':
      case 'admin':
        return true;
      case 'moderator':
        return true;
      case 'resident':
        return serviceRequest.requestedBy.id === userId;
      default:
        return false;
    }
  },

  canCreate: (userRole) => {
    return ['super_admin', 'admin', 'moderator', 'resident'].includes(userRole);
  },
};

// Category mappings for entity types
export const categoryLabels: Record<ServiceRequestCategory, string> = {
  hvac: 'HVAC & Climate Control',
  plumbing: 'Plumbing Services',
  electrical: 'Electrical Work',
  landscaping: 'Landscaping & Grounds',
  cleaning: 'Cleaning Services',
  security: 'Security & Access',
  painting: 'Painting & Decorating',
  carpentry: 'Carpentry & Woodwork',
  general_maintenance: 'General Maintenance',
  pest_control: 'Pest Control',
  appliance_repair: 'Appliance Repair',
  flooring: 'Flooring Services',
  roofing: 'Roofing Services',
  emergency: 'Emergency Services',
  other: 'Other Services',
};

export const priorityLabels: Record<ServiceRequestPriority, string> = {
  low: 'Low Priority',
  medium: 'Medium Priority',
  high: 'High Priority',
  urgent: 'Urgent',
};

export const priorityColors: Record<ServiceRequestPriority, string> = {
  low: 'text-blue-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  urgent: 'text-red-600',
};

export const statusLabels: Record<ServiceRequestStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  open: 'Open',
  in_review: 'In Review',
  approved: 'Approved',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};
